import { useEffect, useState } from 'react';
import {
  getCustomSubjectRequests,
  approveCustomSubject,
  rejectCustomSubject,
} from '@/services/adminApiClient';

interface SubjectRequest {
  id: number;
  name: string;
  status: string;
  request_count: number;
  created_at: string | null;
  reviewed_at: string | null;
}

type TabStatus = 'pending' | 'done' | 'rejected';

const STATUS_BADGE: Record<string, string> = {
  pending:  'bg-amber-100 text-amber-800',
  done:     'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function SubjectRequests() {
  const [tab, setTab] = useState<TabStatus>('pending');
  const [items, setItems] = useState<SubjectRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = (status: TabStatus) => {
    setLoading(true);
    setError('');
    getCustomSubjectRequests(status)
      .then(r => setItems(r.data.items || []))
      .catch(() => setError('Failed to load requests.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(tab); }, [tab]);

  const handleApprove = async (id: number) => {
    setActioning(id);
    setError('');
    try {
      await approveCustomSubject(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      setError('Approval failed. The LLM may have timed out — try again.');
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (id: number) => {
    setActioning(id);
    setError('');
    try {
      await rejectCustomSubject(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      setError('Rejection failed. Please try again.');
    } finally {
      setActioning(null);
    }
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Subject Requests</h1>
        <p className="text-sm text-slate-500 mt-1">
          User-submitted subjects not found in the catalog. Approve to auto-generate a full catalog entry via LLM.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {(['pending', 'done', 'rejected'] as TabStatus[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 text-sm gap-2">
          <span className="text-3xl">📭</span>
          <span>No {tab} requests</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3 text-left">Subject Name</th>
                <th className="px-5 py-3 text-center">Requests</th>
                <th className="px-5 py-3 text-left">Submitted</th>
                <th className="px-5 py-3 text-left">Status</th>
                {tab === 'pending' && <th className="px-5 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-slate-800">{item.name}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 font-semibold text-xs">
                      {item.request_count}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-500">{fmtDate(item.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[item.status] || 'bg-slate-100 text-slate-600'}`}>
                      {item.status}
                    </span>
                  </td>
                  {tab === 'pending' && (
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleApprove(item.id)}
                          disabled={actioning === item.id}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          {actioning === item.id ? 'Adding…' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(item.id)}
                          disabled={actioning === item.id}
                          className="px-3 py-1.5 bg-white hover:bg-red-50 disabled:opacity-50 text-red-600 border border-red-200 text-xs font-medium rounded-lg transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
